import React from 'react';
import PropTypes from 'prop-types';
import './TabBarItem.css';

/* Custom check from Figma (abc/20/feature-check, 9.25×8) — marks a finished step */
const FeatureCheckIcon = require('../../assets/common/icon-feature-check.svg').ReactComponent;

/**
 * TabBarItem — Configurator
 *
 * Icon-over-label tab: the configurator's step rail / bottom bar (Text /
 * Vanliga / Patchar / …) and single actions styled the same way ("Spara").
 * The active item sits on a white tile; a finished step carries a small
 * check to the left of its icon. Without a label it renders icon-only;
 * `compact` folds the label away with a transition (the mobile bar morphing
 * to its compact form on scroll) while it stays the accessible name.
 *
 * Figma component set: 4173:4482 (file "MCP Version"), Brand=Overall —
 * responsive (Resolution is a breakpoint, not a prop):
 *   < 960  Default 4173:4561 | Selected 4173:4571 (pill, P.Label)
 *   > 960  Default 4173:4516 | Hover 4173:4525 | Selected 4173:4552
 *   Checkmark boolean → `done`
 * Not yet: the CLS cap-icon variants.
 *
 * The component renders a plain <button>; pass `role="tab"` +
 * `aria-selected` for a tab rail, or `aria-pressed` for a toggle action.
 */
const TabBarItem = ({
  icon,
  label,
  active = false,
  done = false,
  compact = false,
  onClick,
  className = '',
  ...props
}) => {
  const classes = [
    'cfg-tab-bar-item',
    active && 'cfg-tab-bar-item--active',
    compact && 'cfg-tab-bar-item--compact',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button type="button" className={classes} onClick={onClick} {...props}>
      <span className="cfg-tab-bar-item__icon" aria-hidden="true">
        {icon}
        {done && (
          <span className="cfg-tab-bar-item__check">
            <FeatureCheckIcon />
          </span>
        )}
      </span>
      {label && <span className="cfg-tab-bar-item__label">{label}</span>}
    </button>
  );
};

TabBarItem.propTypes = {
  /** 24px icon node (an <img>, an SVG component or a lucide icon) */
  icon: PropTypes.node,
  /** Label under the icon (P.Label mobile / P.Small desktop); omit for icon-only */
  label: PropTypes.string,
  /** Active state — white tile */
  active: PropTypes.bool,
  /** Finished step — small check to the left of the icon */
  done: PropTypes.bool,
  /** Compact (icon-only, 40px) — the label folds away with a transition */
  compact: PropTypes.bool,
  /** Click handler */
  onClick: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default TabBarItem;
